<template>
  <div class="weetAdvancedEditingAnnotationContainer">
    <div class="colContainer">
      <div class="colLeft">
        <perfect-scrollbar class="annotationListContainer" :options="{suppressScrollX:true}" ref="scrollPanel"
                           v-if="listOfAnnotation.length>0">

          <!-- LIST OF ANNOTATIONS -->
          <CreateAnnotationButton :weet="weet" class="buttonAddList" position="is-bottom-right"/>
          <transition-group name="list-complete">
            <!-- list of annotations -->
            <AnnotationListElement class="list-complete-item" :weet="weet" :annotation="annot"
                                   v-for="annot in listOfAnnotation" style="transition: transform 1s"
                                   :key="annot.annotationID+'-annot'" @delete="deleteAnnot"/>
          </transition-group>
        </perfect-scrollbar>
        <!-- EMPTY STATE -->
        <div v-else class="annotationListContainer emptyState">
          <div class="emptyStateTitle">
            {{ $t('createWeetComponent.advancedEditing.annotationMode.emptyState.title') }}
          </div>
          <div class="emptyStateDescription">
            {{ $t('createWeetComponent.advancedEditing.annotationMode.emptyState.description') }}
          </div>
          <CreateAnnotationButton :weet="weet" class="buttonAddList" :position="'is-top-right'"/>
        </div>
      </div>
      <div class="colMiddle">
        <div class="optionLineTitle" v-if="selectedAnnotation">
          {{ $t('createWeetComponent.advancedEditing.annotationMode.option.title') }}
        </div>
        <AnnotationDurationHelper @onUpdateAnnotation="updateAnnotation" v-if="selectedAnnotation"  />
        <AnnotationColorationHelper @onUpdateAnnotation="updateAnnotationColor" v-if="selectedAnnotation && !isImage"
        :withBackground="isText"/>
      </div>
      <div class="colRight">
        <w-button icon="check" class="buttonSave" :loading="loading|| isAnnotationInteractionSaveInProgress" @click="back">
          {{ $t('createWeetComponent.advancedEditing.validate') }}
        </w-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue} from 'vue-property-decorator';
import {Weet} from '@/store/weet/weetModel';
import WButton from "@/components/wrapper/w-button.vue";
import store from "@/store";
import prettyMilliseconds from "pretty-ms";
import AnnotationListElement from "@/components/recorder/annotation/AnnotationListElement.vue";
import {Annotation, AnnotationType} from "@/store/annotation/annotationModel";
import {
  DELETE_ANNOTATION,
  SELECT_ANNOTATION,
  UNSELECT_ANNOTATION,
  UPDATE_ANNOTATION
} from "@/store/annotation/annotationAction";
import WSwitch from "@/components/wrapper/w-switch.vue";
import WIcon from "@/components/wrapper/w-icon.vue";
import {
  blockClick,
  closeLoading,
  confirmationDanger,
  inform,
  informError,
  showLoading,
  unblockClick
} from "@/utils/dialog";
import WModal from "@/components/wrapper/w-modal.vue";
import CenterCenter from "@/components/layout/CenterCenter.vue";
import IconsSelector from "@/components/icons/IconsSelector.vue";
import {exportNode2PngDataURL} from "@/utils/ImageUtil";
import CreateAnnotationButton from "@/components/recorder/button/CreateAnnotationButton.vue";
import WInput from "@/components/wrapper/w-input.vue";
import AnnotationDurationHelper from "@/components/recorder/annotation/AnnotationDurationHelper.vue";
import WColorPicker from "@/components/wrapper/w-colorPicker.vue";
import AnnotationColorationHelper from "@/components/recorder/annotation/AnnotationColorationHelper.vue";
import debounce from "lodash/debounce";
import {clone} from "@/utils/cloneUtil";
import {UPDATE_ANNOTATION_ON_WEET} from "@/store/myWeet/myWeetAction";
import delay from "delay";
import {ANNOTATION_INTERACTION_SAVE_IN_PROGRESS} from "@/store/recordingState/recordStateAction";


@Component({
  components: {
    AnnotationColorationHelper,
    WColorPicker,
    AnnotationDurationHelper,
    WInput,
    CreateAnnotationButton,
    IconsSelector, CenterCenter, WModal, WIcon, WSwitch, AnnotationListElement, WButton
  },
})
export default class CreateWeetAnnotationMode extends Vue {

  @Prop()
  private weet!: Weet;

  private loading: boolean = false;

  private debounceUpdateAnnotation = debounce((annotation,withImage) => {
    this.updateAnnotation(annotation, withImage);
  }, 250);

  get isAnnotationInteractionSaveInProgress(){
    return store.getters.isAnnotationInteractionSaveInProgress;
  }
  get listOfAnnotation():Annotation[]{
    return this.weet.annotations.filter(a=>a.type!==AnnotationType.INTERACTION)
        .sort((a, b) =>a.time -b.time);
  }

  get selectedAnnotation(): Annotation | undefined {
    const annotation=store.getters.getAnnotationSelected;
    if(annotation?.type===AnnotationType.INTERACTION){
      return undefined;
    }else{
      return annotation
    }
  }

  get isText() {
    return store.getters.getAnnotationSelected?.type === AnnotationType.TEXT;
  }

  get isImage() {
    return store.getters.getAnnotationSelected?.type === AnnotationType.IMAGE;
  }

  private back() {
    this.$emit('back');
  }


  mounted(){
    store.dispatch(UNSELECT_ANNOTATION);
  }



  private updateAnnotationColor(annot:Annotation){
    this.debounceUpdateAnnotation(clone(annot),true)
  }

  private async updateAnnotation(annot: Annotation, withPicture = false) {
    store.dispatch(UPDATE_ANNOTATION_ON_WEET,{weetID:this.weet.weetID,annotation:clone(annot)});

    this.loading = true;
    // export img before update
    var dataUrlImage = '';
    if (withPicture) {
      const annotationNode = document.getElementById('annotation-' + annot.annotationID);
      if (!annotationNode) {
        return;
      }
      // waiting annotation update
      await delay(50);
      dataUrlImage = await exportNode2PngDataURL(annotationNode);
      annot.exportStaticUrl = dataUrlImage;
      store.dispatch(UPDATE_ANNOTATION_ON_WEET,{weetID:this.weet.weetID,annotation:clone(annot)});
    }
    this.loading = false;
    // store.dispatch(SELECT_ANNOTATION,annot)
    this.saveAnnotation(annot)
  }

  private async saveAnnotation(annotationUpdate: Annotation){
    blockClick()
    await store.dispatch(ANNOTATION_INTERACTION_SAVE_IN_PROGRESS, true);
    await store.dispatch(UPDATE_ANNOTATION, {
      weetID: this.weet.weetID, annotation: annotationUpdate
    }).then((backendAnnotation) => {
      inform(this.$t('createWeetComponent.advancedEditing.annotationMode.edit.success').toString());
      store.dispatch(ANNOTATION_INTERACTION_SAVE_IN_PROGRESS, false);
      unblockClick()
    }).catch(() => {
      unblockClick()
      informError(this.$t('createWeetComponent.advancedEditing.annotationMode.edit.error').toString());
      store.dispatch(ANNOTATION_INTERACTION_SAVE_IN_PROGRESS, false);
    })
  }

  private deleteAnnot(annotation:Annotation){
    confirmationDanger(this.$t('createWeetComponent.advancedEditing.annotationMode.delete.confirmMessage').toString(),
        this.$t('createWeetComponent.advancedEditing.annotationMode.delete.confirmTitle').toString()).then(() => {
      store.dispatch(DELETE_ANNOTATION, {weetID: this.weet.weetID, annotation: annotation});
      store.dispatch(UNSELECT_ANNOTATION);
    })
  }

  private beforeDestroy() {
    store.dispatch(UNSELECT_ANNOTATION);
  }
}
</script>

<style scoped lang="scss">
@import '@/scss/shadows.scss';

.weetAdvancedEditingAnnotationContainer {
  margin: auto;
  height: 100%;

  .colContainer {
    display: flex;
    width: 100%;
    height: 100%;
    .colLeft {
      background: var(--light);
      min-width: 320px;
      max-width: 320px;
      text-align: left;
      height: 100%;
      border-radius: 0px 0px 0px 8px;
      animation-duration: 0.2s;
      position: relative;

      .annotationListContainer {
        width: 100%;
        height: 100%;
        max-height: 100%;
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: 40px;

        .buttonAddList {
          width: 100%;
          text-align: center;
          margin-top: 8px;
        }
      }
    }

    .colMiddle {
      text-align: left;
      min-width: 320px;
      max-width: 320px;
      height: 100%;
      .optionLineTitle{
        margin-top: 8px;
        color: var(--black);
        font-size: 18px;
        font-weight: 900;
        padding-left: 16px;
      }
    }

    .colRight {
      .buttonSave {
        position: absolute;
        right: 16px;
        bottom: 16px;
      }
    }
  }

  // EMPTY STATE
  .emptyState {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    flex-direction: column;

    .emptyStateTitle {
      margin-top: 8px;
      font-size: 16px;
      font-weight: 900;
      color: var(--light2);
      text-align: center;
      letter-spacing: 0.3px;
    }

    .emptyStateDescription {
      margin-top: 8px;
      font-size: 16px;
      text-align: center;
      color: var(--light2);
    }

    .emptyStateButtonAdd {
      position: absolute;
      bottom: 32px;
      width: 100%;
      left: 0px;
      text-align: center;
    }
  }
}
</style>
